import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Sparkles, Check, Edit3, Send, Eye, Moon, Sun, Clock, Heart, 
  Utensils, Baby, Zap, Plus, Trash2, GripVertical, Save, X, Smile,
  LayoutDashboard, FileText, PlusCircle, LogOut, Download, Loader2, Star, Cloud,
  Share2, ExternalLink, Copy, ChevronUp, ChevronDown, BookmarkPlus
} from 'lucide-react';
import Logo from '../../components/common/Logo';
import MoonIcon from '../../components/common/MoonIcon';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import apiService, { convertReportToFrontend } from '../../services/api';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Ícones disponíveis para os pilares
const availableIcons = [
  { name: 'Sun', icon: Sun, label: 'Sol' },
  { name: 'Moon', icon: Moon, label: 'Lua' },
  { name: 'Clock', icon: Clock, label: 'Relógio' },
  { name: 'Utensils', icon: Utensils, label: 'Alimentação' },
  { name: 'Baby', icon: Baby, label: 'Bebê' },
  { name: 'Heart', icon: Heart, label: 'Coração' },
  { name: 'Zap', icon: Zap, label: 'Energia' },
  { name: 'Sparkles', icon: Sparkles, label: 'Estrelas' },
  { name: 'Star', icon: Star, label: 'Estrela' },
  { name: 'Cloud', icon: Cloud, label: 'Nuvem' },
];

// Cores disponíveis para os pilares
const availableColors = [
  { name: 'Roxo', bg: '#8F7AC5' },
  { name: 'Rosa', bg: '#D98DAE' },
  { name: 'Salmão', bg: '#F2ACAA' },
  { name: 'Verde', bg: '#7BC47F' },
  { name: 'Azul', bg: '#6BA3D9' },
  { name: 'Amarelo', bg: '#F5C542' },
];

const AdminPlanGeneratorPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving', 'saved', ''
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const planRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const lastSavedContentRef = useRef('');
  
  // URL de compartilhamento
  const shareUrl = `${window.location.origin}/plan/${reportId}`;
  
  // Verificar se plano está aprovado
  const isApproved = report?.status === 'approved';

  // Carregar templates disponíveis
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await apiService.getTemplates();
        setTemplates(data || []);
      } catch (err) {
        console.error('Erro ao carregar templates:', err);
      }
    };
    loadTemplates();
  }, []);

  // Aplicar template selecionado
  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    
    setApplyingTemplate(true);
    try {
      const result = await apiService.applyTemplate(selectedTemplate, reportId);
      if (result.plan) {
        const plan = result.plan;
        setPlanContent({
          babyName: plan.baby_name || report?.babyName || '',
          babyAge: plan.baby_age || report?.babyAge || 0,
          summary: plan.summary || '',
          pillars: plan.pillars || [],
          freeTexts: plan.freeTexts || plan.free_texts || [],
          routine: plan.routine || [],
          priorities: plan.priorities || {},
          expectations: plan.expectations || [],
          finalMessage: plan.finalMessage || plan.final_message || ''
        });
        setGenerated(true);
        setShowTemplateSelector(false);
        toast.success('Template aplicado! Agora você pode editar e aprovar.');
      }
    } catch (err) {
      console.error('Erro ao aplicar template:', err);
      toast.error('Erro ao aplicar template');
    } finally {
      setApplyingTemplate(false);
    }
  };
  
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  
  // Preview: se aprovado, abre URL; se não, abre modal de preview
  const openPreview = () => {
    if (isApproved) {
      window.open(shareUrl, '_blank');
    } else {
      setShowPreviewModal(true);
    }
  };

  // Salvar como template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Digite um nome para o template');
      return;
    }

    setSavingTemplate(true);
    try {
      const planData = {
        baby_name: planContent.babyName,
        baby_age: planContent.babyAge,
        summary: planContent.summary,
        pillars: planContent.pillars,
        freeTexts: planContent.freeTexts || [],
        routine: planContent.routine,
        priorities: planContent.priorities,
        expectations: planContent.expectations,
        finalMessage: planContent.finalMessage
      };

      await apiService.createTemplate({
        name: templateName.trim(),
        description: templateDescription.trim() || null,
        age_range: planContent.babyAge ? `${planContent.babyAge} meses` : null,
        plan_data: planData,
        source_report_id: reportId
      });

      toast.success('Template salvo com sucesso!');
      setShowTemplateModal(false);
      setTemplateName('');
      setTemplateDescription('');
    } catch (err) {
      console.error('Erro ao salvar template:', err);
      toast.error('Erro ao salvar template');
    } finally {
      setSavingTemplate(false);
    }
  };
  
  // Estado do plano
  const [planContent, setPlanContent] = useState({
    babyName: '',
    babyAge: 0,
    summary: '',
    pillars: [],
    freeTexts: [],
    routine: [],
    priorities: {},
    expectations: [],
    finalMessage: ''
  });

  // Carregar dados do backend
  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        const data = await apiService.getReport(reportId);
        const converted = convertReportToFrontend(data);
        setReport(converted);
        
        // Se já existe plano salvo, carregar
        if (converted.sleepPlan || data.sleep_plan) {
          const plan = data.sleep_plan || converted.sleepPlan;
          
          // Garantir que pillars é sempre um array válido
          let pillars = plan.pillars || [];
          if (!Array.isArray(pillars)) {
            console.warn('pillars não é um array, convertendo:', pillars);
            pillars = [];
          }
          
          // Validar cada pilar tem id
          pillars = pillars.map((p, index) => ({
            ...p,
            id: p.id ?? index + 1,
            icon: p.icon || 'Star',
            color: p.color || '#8F7AC5',
            title: p.title || `Pilar ${index + 1}`,
            instructions: Array.isArray(p.instructions) ? p.instructions : [],
            meals: p.meals || null,
            signals: p.signals || null,
            note: p.note || null
          }));
          
          setPlanContent({
            babyName: plan.baby_name || converted.babyName || '',
            babyAge: plan.baby_age || converted.babyAge || 0,
            summary: plan.summary || '',
            pillars: pillars,
            freeTexts: plan.freeTexts || plan.free_texts || [],
            routine: Array.isArray(plan.routine) ? plan.routine : [],
            priorities: plan.priorities || {},
            expectations: Array.isArray(plan.expectations) ? plan.expectations : [],
            finalMessage: plan.finalMessage || plan.final_message || ''
          });
          setGenerated(true);
        } else {
          // Dados iniciais do relatório
          setPlanContent(prev => ({
            ...prev,
            babyName: converted.babyName || '',
            babyAge: converted.babyAge || 0
          }));
        }
      } catch (error) {
        console.error('Erro ao carregar relatório:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadReport();
  }, [reportId]);

  // Também carregar do localStorage como fallback
  useEffect(() => {
    const saved = localStorage.getItem(`plan_${reportId}`);
    if (saved && !generated) {
      try {
        const parsed = JSON.parse(saved);
        setPlanContent(parsed);
        setGenerated(true);
      } catch {
        // ignore
      }
    }
  }, [reportId, generated]);

  // Auto-save quando editando
  const performAutoSave = useCallback(async () => {
    const currentContent = JSON.stringify(planContent);
    
    // Só salvar se houve mudança real
    if (currentContent === lastSavedContentRef.current) return;
    
    try {
      setAutoSaveStatus('saving');
      
      const planData = {
        baby_name: planContent.babyName,
        baby_age: planContent.babyAge,
        summary: planContent.summary,
        pillars: planContent.pillars,
        freeTexts: planContent.freeTexts || [],
        routine: planContent.routine,
        priorities: planContent.priorities,
        expectations: planContent.expectations,
        finalMessage: planContent.finalMessage
      };
      
      await apiService.updateSleepPlan(reportId, planData);
      localStorage.setItem(`plan_${reportId}`, JSON.stringify(planContent));
      
      lastSavedContentRef.current = currentContent;
      setAutoSaveStatus('saved');
      
      // Limpar status após 2 segundos
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (error) {
      console.error('Erro no auto-save:', error);
      setAutoSaveStatus('');
    }
  }, [planContent, reportId]);

  // Disparar auto-save com debounce quando editando
  useEffect(() => {
    if (!editMode || !generated) return;
    
    // Limpar timer anterior
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Configurar novo timer (salva após 2 segundos de inatividade)
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, 2000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [planContent, editMode, generated, performAutoSave]);

  const savePlan = async () => {
    setSaving(true);
    try {
      // Salvar no backend
      const planData = {
        baby_name: planContent.babyName,
        baby_age: planContent.babyAge,
        summary: planContent.summary,
        pillars: planContent.pillars,
        freeTexts: planContent.freeTexts || [],
        routine: planContent.routine,
        priorities: planContent.priorities,
        expectations: planContent.expectations,
        finalMessage: planContent.finalMessage
      };
      
      await apiService.updateSleepPlan(reportId, planData);
      
      // Também salvar no localStorage como backup
      localStorage.setItem(`plan_${reportId}`, JSON.stringify(planContent));
    } catch (error) {
      console.error('Erro ao salvar plano:', error);
      alert('Erro ao salvar plano no servidor');
    } finally {
      setSaving(false);
    }
  };

  // ==================== FUNÇÕES DE EDIÇÃO DOS PILARES ====================
  
  const updatePillar = (pillarId, field, value) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { ...p, [field]: value } : p
      )
    }));
  };

  const addPillarInstruction = (pillarId) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { ...p, instructions: [...(p.instructions || []), ''] } : p
      )
    }));
  };

  const updatePillarInstruction = (pillarId, index, value) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { 
          ...p, 
          instructions: p.instructions.map((inst, i) => i === index ? value : inst) 
        } : p
      )
    }));
  };

  const removePillarInstruction = (pillarId, index) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { 
          ...p, 
          instructions: p.instructions.filter((_, i) => i !== index) 
        } : p
      )
    }));
  };

  // Funções para editar MEALS (refeições)
  const addPillarMeal = (pillarId) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { ...p, meals: [...(p.meals || []), ''] } : p
      )
    }));
  };

  const updatePillarMeal = (pillarId, index, value) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { 
          ...p, 
          meals: p.meals.map((meal, i) => i === index ? value : meal) 
        } : p
      )
    }));
  };

  const removePillarMeal = (pillarId, index) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { 
          ...p, 
          meals: p.meals.filter((_, i) => i !== index) 
        } : p
      )
    }));
  };

  // Funções para editar SIGNALS (sinais de sono)
  const addPillarSignal = (pillarId) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { ...p, signals: [...(p.signals || []), ''] } : p
      )
    }));
  };

  const updatePillarSignal = (pillarId, index, value) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { 
          ...p, 
          signals: p.signals.map((sig, i) => i === index ? value : sig) 
        } : p
      )
    }));
  };

  const removePillarSignal = (pillarId, index) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.map(p => 
        p.id === pillarId ? { 
          ...p, 
          signals: p.signals.filter((_, i) => i !== index) 
        } : p
      )
    }));
  };

  const addPillar = () => {
    const newId = planContent.pillars.length > 0 
      ? Math.max(...planContent.pillars.map(p => p.id)) + 1 
      : 1;
    setPlanContent(prev => ({
      ...prev,
      pillars: [...prev.pillars, {
        id: newId,
        icon: 'Sparkles',
        color: '#8F7AC5',
        title: 'Novo Pilar',
        mainValue: '',
        instructions: ['Instrução 1']
      }]
    }));
  };

  const removePillar = (pillarId) => {
    setPlanContent(prev => ({
      ...prev,
      pillars: prev.pillars.filter(p => p.id !== pillarId)
    }));
  };

  // Reordenar pilares
  const movePillarUp = (index) => {
    if (index === 0) return;
    setPlanContent(prev => {
      const newPillars = [...prev.pillars];
      [newPillars[index - 1], newPillars[index]] = [newPillars[index], newPillars[index - 1]];
      return { ...prev, pillars: newPillars };
    });
  };

  const movePillarDown = (index) => {
    if (index === planContent.pillars.length - 1) return;
    setPlanContent(prev => {
      const newPillars = [...prev.pillars];
      [newPillars[index], newPillars[index + 1]] = [newPillars[index + 1], newPillars[index]];
      return { ...prev, pillars: newPillars };
    });
  };

  // Funções para Texto Livre
  const addFreeText = () => {
    const newId = Date.now();
    setPlanContent(prev => ({
      ...prev,
      freeTexts: [...(prev.freeTexts || []), {
        id: newId,
        title: 'Informação Importante',
        content: '',
        color: '#D98DAE'
      }]
    }));
  };

  const updateFreeText = (textId, field, value) => {
    setPlanContent(prev => ({
      ...prev,
      freeTexts: prev.freeTexts.map(t => 
        t.id === textId ? { ...t, [field]: value } : t
      )
    }));
  };

  const removeFreeText = (textId) => {
    setPlanContent(prev => ({
      ...prev,
      freeTexts: prev.freeTexts.filter(t => t.id !== textId)
    }));
  };

  const moveFreeTextUp = (index) => {
    if (index === 0) return;
    setPlanContent(prev => {
      const newTexts = [...(prev.freeTexts || [])];
      [newTexts[index - 1], newTexts[index]] = [newTexts[index], newTexts[index - 1]];
      return { ...prev, freeTexts: newTexts };
    });
  };

  const moveFreeTextDown = (index) => {
    if (index === (planContent.freeTexts?.length || 0) - 1) return;
    setPlanContent(prev => {
      const newTexts = [...(prev.freeTexts || [])];
      [newTexts[index], newTexts[index + 1]] = [newTexts[index + 1], newTexts[index]];
      return { ...prev, freeTexts: newTexts };
    });
  };

  // Salvar como rascunho
  const saveDraft = async () => {
    setSaving(true);
    try {
      const planData = {
        baby_name: planContent.babyName || '',
        baby_age: planContent.babyAge || 0,
        summary: planContent.summary || '',
        pillars: planContent.pillars || [],
        freeTexts: planContent.freeTexts || [],
        routine: planContent.routine || [],
        priorities: planContent.priorities || {},
        expectations: planContent.expectations || [],
        finalMessage: planContent.finalMessage || ''
      };
      
      // Primeiro salvar como draft
      await apiService.updateDraft(reportId, {
        baby_name: planContent.babyName,
        baby_age: planContent.babyAge,
        sleep_plan: planData
      });
      
      // Também salvar o plano normal
      await apiService.updateSleepPlan(reportId, planData);
      
      alert('Rascunho salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
      alert(`Erro ao salvar rascunho: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setSaving(false);
    }
  };

  // ==================== FUNÇÕES DE EDIÇÃO DA ROTINA ====================
  
  const addRoutineItem = () => {
    setPlanContent(prev => ({
      ...prev,
      routine: [...prev.routine, { time: '', activity: '' }]
    }));
  };

  const updateRoutineItem = (index, field, value) => {
    setPlanContent(prev => ({
      ...prev,
      routine: prev.routine.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeRoutineItem = (index) => {
    setPlanContent(prev => ({
      ...prev,
      routine: prev.routine.filter((_, i) => i !== index)
    }));
  };

  // ==================== FUNÇÕES DE EDIÇÃO DAS PRIORIDADES (SEMANAS DINÂMICAS) ====================
  
  const updatePriority = (week, index, value) => {
    setPlanContent(prev => ({
      ...prev,
      priorities: {
        ...prev.priorities,
        [week]: prev.priorities[week].map((item, i) => i === index ? value : item)
      }
    }));
  };

  const addPriority = (week) => {
    setPlanContent(prev => ({
      ...prev,
      priorities: {
        ...prev.priorities,
        [week]: [...prev.priorities[week], '']
      }
    }));
  };

  const removePriority = (week, index) => {
    setPlanContent(prev => ({
      ...prev,
      priorities: {
        ...prev.priorities,
        [week]: prev.priorities[week].filter((_, i) => i !== index)
      }
    }));
  };

  // Adicionar nova semana
  const addWeek = () => {
    const weekNumbers = Object.keys(planContent.priorities).map(w => {
      const match = w.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    });
    const nextWeek = weekNumbers.length > 0 ? Math.max(...weekNumbers) + 1 : 1;
    const newWeekKey = `Semana ${nextWeek}`;
    
    setPlanContent(prev => ({
      ...prev,
      priorities: {
        ...prev.priorities,
        [newWeekKey]: ['Nova prioridade']
      }
    }));
  };

  // Remover semana
  const removeWeek = (weekKey) => {
    setPlanContent(prev => {
      const newPriorities = { ...prev.priorities };
      delete newPriorities[weekKey];
      return {
        ...prev,
        priorities: newPriorities
      };
    });
  };

  // Renomear semana
  const renameWeek = (oldKey, newKey) => {
    if (oldKey === newKey) return;
    setPlanContent(prev => {
      const newPriorities = {};
      Object.keys(prev.priorities).forEach(key => {
        if (key === oldKey) {
          newPriorities[newKey] = prev.priorities[key];
        } else {
          newPriorities[key] = prev.priorities[key];
        }
      });
      return {
        ...prev,
        priorities: newPriorities
      };
    });
  };

  // ==================== FUNÇÕES DE EDIÇÃO DAS EXPECTATIVAS ====================
  
  const updateExpectation = (index, field, value) => {
    setPlanContent(prev => ({
      ...prev,
      expectations: prev.expectations.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addExpectation = () => {
    setPlanContent(prev => ({
      ...prev,
      expectations: [...prev.expectations, { period: '', desc: '' }]
    }));
  };

  const removeExpectation = (index) => {
    setPlanContent(prev => ({
      ...prev,
      expectations: prev.expectations.filter((_, i) => i !== index)
    }));
  };

  // ==================== GERAR PLANO (mock ou IA) ====================
  
  const handleGenerate = async () => {
    setGenerating(true);
    
    // Tentar gerar com IA primeiro
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/reports/${reportId}/generate-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.plan) {
          // Garantir que pillars é sempre um array válido
          let pillars = data.plan.pillars || [];
          if (!Array.isArray(pillars)) {
            console.warn('pillars da IA não é um array, convertendo:', pillars);
            pillars = [];
          }
          
          // Validar cada pilar tem campos obrigatórios
          pillars = pillars.map((p, index) => ({
            ...p,
            id: p.id ?? index + 1,
            icon: p.icon || 'Star',
            color: p.color || '#8F7AC5',
            title: p.title || `Pilar ${index + 1}`,
            instructions: Array.isArray(p.instructions) ? p.instructions : [],
            meals: p.meals || null,
            signals: p.signals || null,
            note: p.note || null
          }));
          
          const newPlanContent = {
            babyName: data.plan.baby_name || report?.babyName || '',
            babyAge: data.plan.baby_age || report?.babyAge || 0,
            summary: data.plan.summary || '',
            pillars: pillars,
            freeTexts: data.plan.freeTexts || [],
            routine: Array.isArray(data.plan.routine) ? data.plan.routine : [],
            priorities: data.plan.priorities || {},
            expectations: Array.isArray(data.plan.expectations) ? data.plan.expectations : [],
            finalMessage: data.plan.finalMessage || data.plan.final_message || ''
          };
          setPlanContent(newPlanContent);
          setGenerated(true);
          setGenerating(false);
          
          // CORREÇÃO: Salvar no backend IMEDIATAMENTE após geração com IA
          try {
            await apiService.updateSleepPlan(reportId, {
              baby_name: newPlanContent.babyName,
              baby_age: newPlanContent.babyAge,
              summary: newPlanContent.summary,
              pillars: newPlanContent.pillars,
              freeTexts: newPlanContent.freeTexts || [],
              routine: newPlanContent.routine,
              priorities: newPlanContent.priorities,
              expectations: newPlanContent.expectations,
              finalMessage: newPlanContent.finalMessage
            });
            console.log('Plano salvo no backend com sucesso após geração IA');
          } catch (saveErr) {
            console.error('Erro ao salvar plano no backend:', saveErr);
          }
          
          // Salvar no localStorage como backup
          localStorage.setItem(`plan_${reportId}`, JSON.stringify(newPlanContent));
          return;
        }
      }
    } catch (e) {
      console.log('IA não disponível, usando template padrão');
    }
    
    // Fallback: template padrão
    setTimeout(async () => {
      const defaultPlan = generateDefaultPlan();
      setPlanContent(defaultPlan);
      setGenerated(true);
      setGenerating(false);
      
      // Salvar no backend e localStorage
      try {
        await apiService.updateSleepPlan(reportId, {
          baby_name: defaultPlan.babyName,
          baby_age: defaultPlan.babyAge,
          summary: defaultPlan.summary,
          pillars: defaultPlan.pillars,
          freeTexts: defaultPlan.freeTexts || [],
          routine: defaultPlan.routine,
          priorities: defaultPlan.priorities,
          expectations: defaultPlan.expectations,
          finalMessage: defaultPlan.finalMessage
        });
        localStorage.setItem(`plan_${reportId}`, JSON.stringify(defaultPlan));
      } catch (err) {
        console.error('Erro ao salvar plano padrão:', err);
      }
    }, 2000);
  };

  // Gerar plano padrão baseado na idade
  const generateDefaultPlan = () => {
    const babyName = report?.babyName || 'Bebê';
    const babyAge = report?.babyAge || 10;
    const motherName = report?.motherName || 'Mãe';
    
    // Janela de sono baseada na idade
    let sleepWindow = '3h - 3h30';
    let numNaps = '2';
    if (babyAge <= 2) { sleepWindow = '45min - 1h'; numNaps = 'várias'; }
    else if (babyAge <= 4) { sleepWindow = '1h15 - 1h45'; numNaps = '3-4'; }
    else if (babyAge <= 6) { sleepWindow = '1h45 - 2h30'; numNaps = '3'; }
    else if (babyAge <= 8) { sleepWindow = '2h30 - 3h'; numNaps = '2-3'; }
    else if (babyAge <= 10) { sleepWindow = '3h - 3h30'; numNaps = '2'; }
    else if (babyAge <= 14) { sleepWindow = '3h45 - 4h'; numNaps = '1-2'; }
    else { sleepWindow = '4h - 5h'; numNaps = '1'; }

    return {
      babyName,
      babyAge,
      summary: `Com base na análise da rotina de ${babyName}, identifiquei os principais pontos a serem trabalhados para melhorar a qualidade do sono.

**Pontos a observar:**
- Horários de acordar e dormir
- Qualidade das sonecas
- Associações de sono
- Rituais de dormir

Este plano foi elaborado considerando a idade de ${babyAge} meses e as necessidades específicas dessa fase.`,
      pillars: [
        {
          id: 1,
          icon: 'Sun',
          color: '#8F7AC5',
          title: 'Horário Fixo para Acordar',
          mainValue: '07:00 - 07:30',
          instructions: [
            'Manter horário consistente de acordar',
            'Fazer ritual de bom dia com luz solar',
            'Se acordar antes das 6h, manter no quarto escuro'
          ]
        },
        {
          id: 2,
          icon: 'Utensils',
          color: '#D98DAE',
          title: 'Rotina de Alimentação',
          mainValue: '',
          meals: ['Café da manhã', 'Lanche', 'Almoço', 'Lanche', 'Jantar', 'Mamada pré-sono'],
          note: 'Manter alimentação em horários regulares ajuda a regular o sono.'
        },
        {
          id: 3,
          icon: 'Clock',
          color: '#F2ACAA',
          title: 'Janela de Sono',
          mainValue: sleepWindow,
          signals: ['Boceja', 'Coça os olhos', 'Fica irritado', 'Olhar vidrado'],
          instructions: [
            'Observar sinais de sono',
            'Respeitar a janela de sono da idade'
          ]
        },
        {
          id: 4,
          icon: 'Baby',
          color: '#7BC47F',
          title: 'Sonecas',
          mainValue: `${numNaps} soneca(s)/dia`,
          instructions: [
            'Encerrar última soneca até 15:30-16:00',
            'Priorizar sonecas reparadoras',
            'Ambiente adequado para sonecas'
          ]
        },
        {
          id: 5,
          icon: 'Zap',
          color: '#6BA3D9',
          title: 'Gasto de Energia',
          mainValue: 'Foco: última janela',
          instructions: [
            'Atividades ao ar livre',
            'Brincadeiras no chão',
            'Gastar energia antes de dormir'
          ]
        },
        {
          id: 6,
          icon: 'Heart',
          color: '#D98DAE',
          title: 'Ritual de Sono',
          mainValue: '30-40 min antes de dormir',
          instructions: [
            'Sequência consistente: Banho → Mamada → Quarto escuro → Dormir',
            'Usar objeto de transição se tiver',
            'Criar ambiente calmo e acolhedor'
          ]
        }
      ],
      routine: [
        { time: '07:00', activity: 'Acordar + ritual de bom dia + luz solar' },
        { time: '07:30', activity: 'Mamada/café da manhã' },
        { time: '08:30', activity: 'Brincadeiras' },
        { time: '09:30', activity: '1ª Soneca' },
        { time: '10:30', activity: 'Acordar + mamada/lanche' },
        { time: '12:00', activity: 'Almoço' },
        { time: '13:30', activity: '2ª Soneca' },
        { time: '15:30', activity: 'Acordar + mamada/lanche' },
        { time: '16:00', activity: 'Atividades + gastar energia' },
        { time: '17:30', activity: 'Jantar' },
        { time: '18:15', activity: 'Banho' },
        { time: '18:45', activity: 'Mamada + ritual de sono' },
        { time: '19:30', activity: 'Sono noturno' }
      ],
      priorities: {
        'Semana 1': [
          'Estabelecer horário fixo de acordar',
          'Criar ritual de sono consistente',
          'Observar e respeitar sinais de sono'
        ],
        'Semana 2': [
          'Trabalhar associações de sono',
          'Manter consistência nas sonecas',
          'Ajustar horários conforme necessário'
        ],
        'Semana 3': [
          'Consolidar a rotina',
          'Estimular autonomia de sono',
          'Ajustar detalhes finais'
        ]
      },
      expectations: [
        { period: '3-5 dias', desc: 'Adaptação inicial - pode haver resistência' },
        { period: '1 semana', desc: 'Primeiras melhoras visíveis' },
        { period: '2-3 semanas', desc: 'Rotina se estabelecendo' },
        { period: '1 mês', desc: 'Mudanças consolidadas' }
      ],
      finalMessage: `${motherName}, cada bebê é único e ${babyName} está em um momento especial de desenvolvimento. Este plano é um guia, e juntas vamos adaptá-lo conforme a resposta do seu bebê. Estou aqui para ajudar! 💜`
    };
  };

  const handleApprove = async () => {
    savePlan();
    try {
      await apiService.approvePlan(reportId);
    } catch (e) {
      console.error('Erro ao aprovar:', e);
    }
    navigate('/admin/dashboard');
  };

  // ==================== GERAR PDF ====================
  
  const generatePDF = async () => {
    setGeneratingPdf(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      
      // Cores da identidade visual (baseado no PDF "Bloco de Notas")
      const roxo = [143, 122, 197];         // #8F7AC5 - Roxo principal
      const roxoEscuro = [77, 58, 119];     // #4D3A77 - Roxo escuro (header)
      const rosa = [217, 141, 174];         // #D98DAE - Rosa
      const pessego = [250, 224, 213];      // #FAE0D5 - Pêssego
      const roxoClaro = [107, 91, 140];     // #6B5B8C - Roxo claro texto
      const bgClaro = [251, 244, 255];      // #FBF4FF - Fundo claro
      const lavanda = [211, 196, 231];      // #D3C4E7 - Lavanda fundo padrão
      const rosaSuave = [247, 204, 217];    // #F7CCD9 - Rosa suave
      const laranjaClaro = [240, 192, 160]; // #F0C0A0 - Laranja claro estrelas
      const branco = [255, 255, 255];
      const roxoTexto = [48, 36, 79];       // #30244F - Texto principal
      
      // Helper para converter hex para RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
        return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : roxo;
      };
      
      // Helper para desenhar estrela de 4 pontas
      const drawStar = (x, y, size, color) => {
        pdf.setFillColor(...color);
        // Estrela simplificada como diamante
        pdf.triangle(x, y - size, x + size * 0.3, y, x, y + size, 'F');
        pdf.triangle(x, y - size, x - size * 0.3, y, x, y + size, 'F');
        pdf.triangle(x - size, y, x, y - size * 0.3, x + size, y, 'F');
        pdf.triangle(x - size, y, x, y + size * 0.3, x + size, y, 'F');
      };
      
      // Helper para desenhar lua crescente
      const drawMoon = (x, y, size, color, bgColor) => {
        pdf.setFillColor(...color);
        pdf.circle(x, y, size, 'F');
        pdf.setFillColor(...bgColor);
        pdf.circle(x + size * 0.4, y - size * 0.15, size * 0.85, 'F');
      };
      
      // Helper para desenhar ponto/círculo pequeno
      const drawDot = (x, y, size, color) => {
        pdf.setFillColor(...color);
        pdf.circle(x, y, size, 'F');
      };
      
      // Helper para adicionar padrão celestial nas bordas
      const addCelestialPattern = (density = 'normal', reduceMoonsRight = false) => {
        const elements = [];
        const spacing = density === 'light' ? 25 : 15;
        
        // Gerar posições para elementos decorativos nas bordas
        for (let i = 0; i < pageHeight; i += spacing) {
          // Borda esquerda
          if (Math.random() > 0.5) {
            elements.push({ type: Math.random() > 0.6 ? 'moon' : (Math.random() > 0.5 ? 'star' : 'dot'), x: 5 + Math.random() * 10, y: i + Math.random() * 10 });
          }
          // Borda direita - menos luas se reduceMoonsRight for true
          if (Math.random() > 0.5) {
            let elementType;
            if (reduceMoonsRight) {
              // Apenas estrelas e pontos no lado direito da página 1
              elementType = Math.random() > 0.5 ? 'star' : 'dot';
            } else {
              elementType = Math.random() > 0.6 ? 'moon' : (Math.random() > 0.5 ? 'star' : 'dot');
            }
            elements.push({ type: elementType, x: pageWidth - 5 - Math.random() * 10, y: i + Math.random() * 10 });
          }
        }
        
        // Borda superior
        for (let i = 20; i < pageWidth - 20; i += spacing) {
          if (Math.random() > 0.6) {
            elements.push({ type: Math.random() > 0.5 ? 'star' : 'dot', x: i + Math.random() * 10, y: 5 + Math.random() * 8 });
          }
        }
        
        // Borda inferior
        for (let i = 20; i < pageWidth - 20; i += spacing) {
          if (Math.random() > 0.6) {
            elements.push({ type: Math.random() > 0.5 ? 'star' : 'dot', x: i + Math.random() * 10, y: pageHeight - 5 - Math.random() * 8 });
          }
        }
        
        // Desenhar elementos
        elements.forEach(el => {
          const colors = [roxo, rosa, laranjaClaro, rosaSuave];
          const color = colors[Math.floor(Math.random() * colors.length)];
          
          if (el.type === 'moon') {
            drawMoon(el.x, el.y, 2 + Math.random() * 1.5, color, bgClaro);
          } else if (el.type === 'star') {
            drawStar(el.x, el.y, 1.5 + Math.random() * 1, color);
          } else {
            drawDot(el.x, el.y, 0.5 + Math.random() * 0.5, color);
          }
        });
      };
      
      // Helper para adicionar logo "De mãe pra mãe" estilizado
      const addLogo = (x, y, size = 1) => {
        // Lua principal
        drawMoon(x, y, 5 * size, branco, bgClaro);
        // Estrelinhas ao redor
        drawStar(x - 7 * size, y - 3 * size, 1.2 * size, pessego);
        drawDot(x + 6 * size, y + 4 * size, 0.8 * size, rosa);
        drawDot(x - 4 * size, y + 5 * size, 0.6 * size, laranjaClaro);
      };
      
      // Helper para verificar quebra de página
      const checkPageBreak = (neededSpace) => {
        if (yPosition + neededSpace > pageHeight - 25) {
          pdf.addPage();
          // Fundo lavanda suave
          pdf.setFillColor(...bgClaro);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          // Padrão celestial leve
          addCelestialPattern('light');
          yPosition = margin + 5;
          // Logo no canto superior direito
          addLogo(pageWidth - 18, 12, 0.7);
          return true;
        }
        return false;
      };
      
      // ==================== PÁGINA 1: CAPA ====================
      
      // Fundo lavanda suave (como no Bloco de Notas)
      pdf.setFillColor(...bgClaro);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Gradiente no topo (simulado com retângulos)
      pdf.setFillColor(...roxoEscuro);
      pdf.rect(0, 0, pageWidth, 8, 'F');
      pdf.setFillColor(100, 80, 150);
      pdf.rect(0, 8, pageWidth, 6, 'F');
      pdf.setFillColor(130, 110, 180);
      pdf.rect(0, 14, pageWidth, 4, 'F');
      
      // Padrão celestial nas bordas - menos luas no lado direito da página 1
      addCelestialPattern('normal', true);
      
      // Header principal roxo
      pdf.setFillColor(...roxo);
      pdf.roundedRect(margin - 5, 30, contentWidth + 10, 55, 8, 8, 'F');
      
      // Detalhe rosa abaixo do header
      pdf.setFillColor(...rosa);
      pdf.roundedRect(margin, 85, contentWidth, 6, 3, 3, 'F');
      
      // Logo no header
      addLogo(pageWidth - 30, 50, 1);
      
      // Título principal
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PLANO DE SONO', margin + 10, 50);
      
      // Subtítulo "Personalizado"
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Personalizado', margin + 10, 60);
      
      // Nome do bebê
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(planContent.babyName.toUpperCase(), margin + 10, 75);
      
      // Idade
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${planContent.babyAge} meses`, margin + 10, 82);
      
      yPosition = 105;
      
      // Seção de Análise
      pdf.setFillColor(...pessego);
      pdf.roundedRect(margin, yPosition, contentWidth, 15, 4, 4, 'F');
      
      // Ícone de lua na seção
      drawMoon(margin + 10, yPosition + 7.5, 4, roxo, pessego);
      
      pdf.setTextColor(...roxoTexto);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Análise da Situação Atual', margin + 20, yPosition + 10);
      yPosition += 22;
      
      // Texto da análise
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...roxoClaro);
      const summaryLines = pdf.splitTextToSize(planContent.summary.replace(/\*\*/g, ''), contentWidth);
      summaryLines.forEach(line => {
        checkPageBreak(6);
        // Texto justificado - calcular espaçamento
        pdf.text(line, margin, yPosition, { align: 'justify', maxWidth: contentWidth });
        yPosition += 5.5;
      });
      
      // Textos Livres (informações importantes)
      if (planContent.freeTexts && planContent.freeTexts.length > 0) {
        yPosition += 10;
        planContent.freeTexts.forEach((freeText) => {
          const freeTextColor = hexToRgb(freeText.color || '#D98DAE');
          const contentText = (freeText.content || '').replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();
          // Largura máxima: apenas desconta barra lateral (8px) e margem interna (5px)
          const freeTextWidth = contentWidth - 15;
          const contentLines = pdf.splitTextToSize(contentText, freeTextWidth);
          const freeTextHeight = 18 + (contentLines.length * 4.5);
          
          checkPageBreak(freeTextHeight + 10);
          
          // Card com borda colorida
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(margin, yPosition, contentWidth, freeTextHeight, 4, 4, 'F');
          
          // Barra lateral colorida
          pdf.setFillColor(...freeTextColor);
          pdf.roundedRect(margin, yPosition, 4, freeTextHeight, 2, 0, 'F');
          
          // Título
          pdf.setTextColor(...freeTextColor);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text(freeText.title || 'Informação Importante', margin + 8, yPosition + 8);
          
          // Conteúdo - texto justificado ocupando toda largura
          pdf.setTextColor(...roxoClaro);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          let freeTextY = yPosition + 14;
          contentLines.forEach(line => {
            pdf.text(line, margin + 8, freeTextY, { align: 'justify', maxWidth: freeTextWidth });
            freeTextY += 4.5;
          });
          
          yPosition += freeTextHeight + 6;
        });
      }
      
      // ==================== PÁGINA 2: PILARES ====================
      pdf.addPage();
      pdf.setFillColor(...bgClaro);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      addCelestialPattern('light');
      
      // Header rosa
      pdf.setFillColor(...rosa);
      pdf.roundedRect(0, 0, pageWidth, 35, 0, 0, 'F');
      
      // Elementos decorativos no header
      drawMoon(pageWidth - 25, 18, 5, branco, rosa);
      drawStar(25, 12, 2, branco);
      drawDot(35, 25, 1, pessego);
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Os Pilares do Sono', pageWidth / 2, 22, { align: 'center' });
      
      yPosition = 50;
      
      // Pilares - calcular altura dinâmica baseada no conteúdo
      planContent.pillars.forEach((pillar, index) => {
        const pillarColor = hexToRgb(pillar.color);
        const items = pillar.instructions || pillar.meals || pillar.signals || [];
        
        // IMPORTANTE: Definir fonte ANTES de calcular quebras de linha
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        
        // Largura máxima do texto: ocupa quase toda a largura do card
        const pillarTextWidth = contentWidth - 16;
        let totalItemsHeight = 0;
        const itemsWithLines = items.map(item => {
          const cleanItem = item.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').replace(/[★●◆▸→]/g, '').trim();
          const lines = pdf.splitTextToSize(cleanItem, pillarTextWidth);
          totalItemsHeight += lines.length * 4;
          return { text: cleanItem, lines };
        });
        
        // Calcular altura da nota adicional se existir
        let noteLines = [];
        let noteHeight = 0;
        if (pillar.note) {
          const cleanNote = pillar.note.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();
          noteLines = pdf.splitTextToSize(cleanNote, pillarTextWidth - 10);
          noteHeight = noteLines.length * 4 + 6;
        }
        
        const pillarHeight = 26 + totalItemsHeight + (items.length * 2) + noteHeight;
        
        checkPageBreak(pillarHeight + 10);
        
        // Card do pilar com borda colorida
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(margin, yPosition, contentWidth, pillarHeight, 5, 5, 'F');
        
        // Barra lateral colorida
        pdf.setFillColor(...pillarColor);
        pdf.roundedRect(margin, yPosition, 5, pillarHeight, 3, 0, 'F');
        
        // Ícone (círculo colorido)
        pdf.setFillColor(...pillarColor);
        pdf.circle(margin + 14, yPosition + 11, 5, 'F');
        
        // Título do pilar - largura máxima
        pdf.setTextColor(...pillarColor);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        const titleLines = pdf.splitTextToSize(pillar.title, contentWidth - 24);
        pdf.text(titleLines[0], margin + 22, yPosition + 9);
        
        // Valor principal
        if (pillar.mainValue) {
          pdf.setTextColor(...roxoTexto);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.text(pillar.mainValue, margin + 22, yPosition + 15);
        }
        
        // Instruções - texto completo ocupando TODA a largura
        pdf.setTextColor(...roxoClaro);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        let itemY = yPosition + 22;
        
        itemsWithLines.forEach((itemData, i) => {
          // Bullet point colorido
          pdf.setFillColor(...pillarColor);
          pdf.circle(margin + 10, itemY, 1, 'F');
          
          // Texto com múltiplas linhas - largura TOTAL do card
          pdf.setTextColor(...roxoClaro);
          itemData.lines.forEach((line, lineIndex) => {
            pdf.text(line, margin + 14, itemY + (lineIndex * 4));
          });
          
          itemY += (itemData.lines.length * 4) + 2;
        });
        
        // Nota adicional (se existir)
        if (pillar.note && noteLines.length > 0) {
          itemY += 2;
          
          // Fundo destacado para a nota
          pdf.setFillColor(...pessego);
          pdf.roundedRect(margin + 6, itemY - 2, contentWidth - 12, noteHeight - 2, 3, 3, 'F');
          
          pdf.setTextColor(...roxoTexto);
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'italic');
          noteLines.forEach((line, lineIndex) => {
            pdf.text(line, margin + 10, itemY + 2 + (lineIndex * 4));
          });
        }
        
        yPosition += pillarHeight + 6;
      });
      
      // ==================== PÁGINA 3: ROTINA SUGERIDA ====================
      if (planContent.routine && planContent.routine.length > 0) {
        pdf.addPage();
        pdf.setFillColor(...bgClaro);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        addCelestialPattern('light');
        
        // Header rosa
        pdf.setFillColor(...rosa);
        pdf.roundedRect(0, 0, pageWidth, 35, 0, 0, 'F');
        
        drawMoon(pageWidth - 25, 18, 5, branco, rosa);
        drawStar(25, 12, 2, branco);
        drawDot(35, 25, 1, pessego);
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Rotina Sugerida', pageWidth / 2, 22, { align: 'center' });
        
        yPosition = 45;
        
        // Timeline de rotina - espaçamento compacto para caber 14 itens
        const routineItems = planContent.routine;
        const routineItemHeight = Math.min(14, Math.floor((pageHeight - 70) / Math.max(routineItems.length, 1)));
        
        routineItems.forEach((item, index) => {
          // Card branco para cada item
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(margin, yPosition, contentWidth, routineItemHeight - 2, 3, 3, 'F');
          
          // Bolinha de timeline colorida
          const timelineColor = index % 2 === 0 ? roxo : rosa;
          pdf.setFillColor(...timelineColor);
          pdf.circle(margin + 10, yPosition + (routineItemHeight - 2) / 2, 3, 'F');
          
          // Linha conectora
          if (index < routineItems.length - 1) {
            pdf.setDrawColor(...lavanda);
            pdf.setLineWidth(1.5);
            pdf.line(margin + 10, yPosition + routineItemHeight - 2, margin + 10, yPosition + routineItemHeight + 1);
          }
          
          // Horário
          pdf.setTextColor(...roxoTexto);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text(item.time || '', margin + 16, yPosition + (routineItemHeight - 2) / 2 + 1);
          
          // Atividade - largura máxima (desconta bolinha timeline + horário)
          pdf.setTextColor(...roxoClaro);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          const activityText = (item.activity || '').replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim();
          const activityWidth = contentWidth - 38;
          const activityLines = pdf.splitTextToSize(activityText, activityWidth);
          pdf.text(activityLines[0], margin + 35, yPosition + (routineItemHeight - 2) / 2 + 1);
          
          yPosition += routineItemHeight;
        });
      }
      
      // ==================== PÁGINA 4: PRIORIDADES ====================
      pdf.addPage();
      pdf.setFillColor(...bgClaro);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      addCelestialPattern('light');
      
      // Header roxo
      pdf.setFillColor(...roxo);
      pdf.roundedRect(0, 0, pageWidth, 35, 0, 0, 'F');
      
      drawMoon(pageWidth - 25, 18, 5, branco, roxo);
      drawStar(25, 12, 2, pessego);
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Prioridades por Semana', pageWidth / 2, 22, { align: 'center' });
      
      yPosition = 50;
      
      // Prioridades por semana - largura TOTAL do texto
      Object.entries(planContent.priorities || {}).forEach(([week, items], weekIndex) => {
        if (!items || items.length === 0) return;
        
        // IMPORTANTE: Definir fonte ANTES de calcular quebras de linha
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        
        // Largura máxima: ocupa quase toda a largura do card
        const priorityTextWidth = contentWidth - 14;
        let totalItemsHeight = 0;
        const itemsWithLines = items.map(item => {
          const cleanItem = item.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').replace(/[★●◆▸→]/g, '').trim();
          const lines = pdf.splitTextToSize(cleanItem, priorityTextWidth);
          totalItemsHeight += lines.length * 4;
          return { text: cleanItem, lines };
        });
        
        const weekHeight = 20 + totalItemsHeight + (items.length * 2.5);
        
        checkPageBreak(weekHeight + 10);
        
        // Card da semana
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(margin, yPosition, contentWidth, weekHeight, 5, 5, 'F');
        
        // Header da semana
        const weekColor = weekIndex % 2 === 0 ? roxo : rosa;
        pdf.setFillColor(...weekColor);
        pdf.roundedRect(margin, yPosition, contentWidth, 11, 5, 5, 'F');
        pdf.rect(margin, yPosition + 5, contentWidth, 6, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(week, margin + 6, yPosition + 7);
        
        // Itens - texto completo ocupando TODA largura
        let itemY = yPosition + 16;
        itemsWithLines.forEach((itemData, i) => {
          // Checkbox estilizado
          pdf.setDrawColor(...weekColor);
          pdf.setLineWidth(0.3);
          pdf.roundedRect(margin + 6, itemY - 2, 3.5, 3.5, 1, 1, 'S');
          
          pdf.setTextColor(...roxoClaro);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          
          // Todas as linhas do item - largura TOTAL
          itemData.lines.forEach((line, lineIndex) => {
            pdf.text(line, margin + 12, itemY + (lineIndex * 4));
          });
          
          itemY += (itemData.lines.length * 4) + 2.5;
        });
        
        yPosition += weekHeight + 6;
      });
      
      // Expectativas
      checkPageBreak(60);
      yPosition += 10;
      
      pdf.setFillColor(...pessego);
      pdf.roundedRect(margin, yPosition, contentWidth, 12, 4, 4, 'F');
      drawStar(margin + 8, yPosition + 6, 2, roxo);
      
      pdf.setTextColor(...roxoTexto);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Expectativas Realistas', margin + 16, yPosition + 8);
      yPosition += 18;
      
      (planContent.expectations || []).forEach(exp => {
        // Largura máxima: desconta badge período (45)
        const expTextWidth = contentWidth - 48;
        const descLines = pdf.splitTextToSize(exp.desc || '', expTextWidth);
        const expHeight = Math.max(12, descLines.length * 4 + 5);
        
        checkPageBreak(expHeight + 4);
        
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(margin, yPosition, contentWidth, expHeight, 3, 3, 'F');
        
        // Badge do período
        pdf.setFillColor(...roxo);
        pdf.roundedRect(margin + 3, yPosition + 2, 40, 7, 2, 2, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.text(exp.period || '', margin + 5, yPosition + 6.5);
        
        // Descrição - largura máxima
        pdf.setTextColor(...roxoClaro);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        descLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 46, yPosition + 6.5 + (lineIndex * 4));
        });
        
        yPosition += expHeight + 2;
      });
      
      // Mensagem final
      if (planContent.finalMessage) {
        checkPageBreak(50);
        yPosition += 12;
        
        const cleanMessage = planContent.finalMessage
          .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
          .replace(/[\u{2600}-\u{26FF}]/gu, '')
          .replace(/[\u{2700}-\u{27BF}]/gu, '')
          .replace(/💜|❤️|😊|🌙|⭐|✨/g, '')
          .trim();
        
        // Largura máxima: desconta lua (30) + margem direita (10)
        const msgTextWidth = contentWidth - 42;
        const msgLines = pdf.splitTextToSize(cleanMessage, msgTextWidth);
        const msgHeight = Math.max(msgLines.length * 4.5 + 22, 40);
        
        // Card da mensagem
        pdf.setFillColor(...lavanda);
        pdf.roundedRect(margin, yPosition, contentWidth, msgHeight, 6, 6, 'F');
        
        // Borda decorativa
        pdf.setDrawColor(...rosa);
        pdf.setLineWidth(1);
        pdf.roundedRect(margin + 2, yPosition + 2, contentWidth - 4, msgHeight - 4, 5, 5, 'S');
        
        // Lua decorativa à esquerda
        drawMoon(margin + 14, yPosition + msgHeight / 2, 6, roxo, lavanda);
        
        // Estrelas decorativas
        drawStar(pageWidth - margin - 10, yPosition + 10, 1.5, rosa);
        drawDot(pageWidth - margin - 18, yPosition + msgHeight - 12, 1, pessego);
        
        // Texto - largura máxima
        pdf.setTextColor(...roxoTexto);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'italic');
        msgLines.forEach((line, i) => {
          pdf.text(line, margin + 30, yPosition + 10 + (i * 4.5));
        });
        
        // Assinatura
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(...rosa);
        pdf.text('De mãe pra mãe, com carinho', pageWidth - margin - 5, yPosition + msgHeight - 5, { align: 'right' });
      }
      
      // Rodapé em todas as páginas
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        
        // Linha decorativa no rodapé
        pdf.setDrawColor(...rosa);
        pdf.setLineWidth(0.5);
        pdf.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        
        // Lua no rodapé
        drawMoon(pageWidth - margin - 8, pageHeight - 8, 3, roxo, bgClaro);
        
        // Apenas numeração de páginas (removido marimello.com.br)
        pdf.setTextColor(...roxoClaro);
        pdf.setFontSize(8);
        pdf.text(`${i}/${totalPages}`, pageWidth / 2, pageHeight - 6, { align: 'center' });
      }
      
      pdf.save(`plano-sono-${planContent.babyName.toLowerCase().replace(/\s+/g, '-')}.pdf`);
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getIconComponent = (iconName) => {
    const found = availableIcons.find(i => i.name === iconName);
    return found ? found.icon : Sparkles;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF4FF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Header */}
      <header className="bg-white border-b border-[#FAE0D5] px-6 py-4 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/reports/${reportId}`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" className="cursor-pointer hover:opacity-80" />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {generated && (
              <>
                {saving ? (
                  <span className="text-sm text-[#7BC47F] flex items-center gap-1">
                    <Save className="w-4 h-4 animate-pulse" />
                    Salvando...
                  </span>
                ) : autoSaveStatus === 'saving' ? (
                  <span className="text-sm text-[#8F7AC5] flex items-center gap-1">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvando...
                  </span>
                ) : autoSaveStatus === 'saved' ? (
                  <span className="text-sm text-[#7BC47F] flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Salvo automaticamente
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={saveDraft}
                    className="text-[#F5A623]"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Salvar como Rascunho
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(!editMode)}
                  className={editMode ? 'bg-[#8F7AC5] text-white' : ''}
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  {editMode ? 'Visualizar' : 'Editar'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generatePDF}
                  disabled={generatingPdf}
                  className="border-[#D98DAE] text-[#D98DAE]"
                >
                  {generatingPdf ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-1" />
                  )}
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openPreview}
                  className="border-[#8F7AC5] text-[#8F7AC5]"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Ver como Mãe
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowShareModal(true)}
                  className="border-[#6BA3D9] text-[#6BA3D9]"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Compartilhar
                </Button>
                {isApproved && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTemplateName(`${planContent.babyAge} meses - ${planContent.babyName}`);
                      setShowTemplateModal(true);
                    }}
                    className="border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623] hover:text-white"
                  >
                    <BookmarkPlus className="w-4 h-4 mr-1" />
                    Salvar Template
                  </Button>
                )}
                {isApproved ? (
                  <Button
                    disabled
                    className="bg-[#7BC47F]/50 cursor-not-allowed"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Já Aprovado
                  </Button>
                ) : (
                  <Button
                    onClick={handleApprove}
                    className="bg-[#7BC47F] hover:bg-[#5BA35F]"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Aprovar
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8" ref={planRef}>
        {!generated ? (
          // Tela de pré-geração
          <div className="text-center py-12">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-3xl p-8 shadow-sm max-w-md mx-auto"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] rounded-full flex items-center justify-center mx-auto mb-6">
                <MoonIcon className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#30244F] mb-2">
                Gerar Plano Personalizado
              </h2>
              <p className="text-[#6B5B8C] mb-6">
                {report?.babyName || 'Bebê'} • {report?.babyAge || '?'} meses
              </p>
              
              {/* Info do relatório */}
              <div className="bg-[#FBF4FF] rounded-2xl p-4 mb-6 text-left">
                <p className="text-sm text-[#6B5B8C]">
                  <strong>Dias registrados:</strong> {report?.completedDays || 0}/5
                </p>
                {report?.initialForm && (
                  <p className="text-sm text-[#6B5B8C] mt-2">
                    <strong>Como dorme:</strong> {report.initialForm.howBabySleeps || 'Não informado'}
                  </p>
                )}
              </div>
              
              {isApproved ? (
                <div className="text-center">
                  <div className="bg-[#7BC47F]/10 border border-[#7BC47F] rounded-xl p-4 mb-4">
                    <Check className="w-8 h-8 text-[#7BC47F] mx-auto mb-2" />
                    <p className="text-[#7BC47F] font-semibold">Plano já aprovado!</p>
                    <p className="text-sm text-[#6B5B8C] mt-1">
                      Este plano já foi aprovado e está disponível para a mãe.
                    </p>
                  </div>
                  <Button
                    onClick={() => setGenerated(true)}
                    variant="outline"
                    className="border-[#8F7AC5] text-[#8F7AC5]"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver/Editar Plano Existente
                  </Button>
                </div>
              ) : showTemplateSelector ? (
                // Seletor de Template
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-[#30244F]">Escolha um Template</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowTemplateSelector(false)}
                      className="text-[#9890A8]"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Voltar
                    </Button>
                  </div>
                  
                  {templates.length === 0 ? (
                    <div className="text-center py-6">
                      <Copy className="w-10 h-10 text-[#D98DAE] mx-auto mb-2" />
                      <p className="text-[#6B5B8C]">Nenhum template disponível</p>
                      <p className="text-xs text-[#9890A8] mt-1">
                        Aprove um plano e salve como template para usar aqui.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {templates.map((t) => (
                        <div
                          key={t.id}
                          onClick={() => setSelectedTemplate(t.id)}
                          className={`p-3 rounded-xl border cursor-pointer transition-all ${
                            selectedTemplate === t.id
                              ? 'border-[#8F7AC5] bg-[#8F7AC5]/5'
                              : 'border-[#FAE0D5] hover:border-[#8F7AC5]/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-[#30244F]">{t.name}</p>
                              {t.age_range && (
                                <span className="text-xs text-[#8F7AC5]">{t.age_range}</span>
                              )}
                            </div>
                            {selectedTemplate === t.id && (
                              <Check className="w-5 h-5 text-[#8F7AC5]" />
                            )}
                          </div>
                          {t.description && (
                            <p className="text-xs text-[#6B5B8C] mt-1 line-clamp-2">{t.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button
                    onClick={handleApplyTemplate}
                    disabled={!selectedTemplate || applyingTemplate}
                    className="w-full bg-[#8F7AC5] hover:bg-[#7A68A8] text-white"
                  >
                    {applyingTemplate ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Aplicando...
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Usar Template Selecionado
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                // Opções de geração
                <div className="space-y-3">
                  <Button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="w-full bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] hover:opacity-90 text-white px-8 py-6 text-lg"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Gerando plano...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Gerar com IA
                      </>
                    )}
                  </Button>
                  
                  {templates.length > 0 && (
                    <Button
                      onClick={() => setShowTemplateSelector(true)}
                      variant="outline"
                      className="w-full border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623] hover:text-white py-6 text-lg"
                    >
                      <Copy className="w-5 h-5 mr-2" />
                      Usar Template ({templates.length})
                    </Button>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        ) : (
          // Plano gerado
          <div className="space-y-8">
            {/* Header do Plano */}
            <div className="bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] rounded-3xl p-8 text-center text-white">
              <h2 className="text-3xl font-bold mb-2">Plano de Sono Personalizado</h2>
              {editMode ? (
                <div className="flex items-center justify-center gap-2">
                  <Input 
                    value={planContent.babyName}
                    onChange={(e) => setPlanContent({...planContent, babyName: e.target.value})}
                    className="bg-white/20 border-white/30 text-white placeholder-white/50 text-center w-40"
                  />
                  <span>•</span>
                  <Input 
                    value={planContent.babyAge}
                    onChange={(e) => setPlanContent({...planContent, babyAge: e.target.value})}
                    className="bg-white/20 border-white/30 text-white placeholder-white/50 text-center w-24"
                  />
                  <span>meses</span>
                </div>
              ) : (
                <p className="text-xl text-white/80">{planContent.babyName} • {planContent.babyAge} meses</p>
              )}
            </div>

            {/* Summary */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#30244F] mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#8F7AC5]" />
                Análise da Situação Atual
              </h3>
              {editMode ? (
                <Textarea
                  value={planContent.summary}
                  onChange={(e) => setPlanContent({ ...planContent, summary: e.target.value })}
                  className="min-h-[150px] border-[#FAE0D5]"
                />
              ) : (
                <p className="text-[#6B5B8C] whitespace-pre-line">{planContent.summary}</p>
              )}
            </div>

            {/* Pillars */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#30244F] text-lg">Pilares do Plano</h3>
                {editMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addPillar}
                    className="border-[#8F7AC5] text-[#8F7AC5]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Pilar
                  </Button>
                )}
              </div>
              
              <div className="space-y-6 max-w-4xl mx-auto">
                {planContent.pillars.map((pillar, pillarIndex) => {
                  const IconComponent = getIconComponent(pillar.icon);
                  return (
                    <div key={pillar.id} className="bg-white rounded-3xl p-6 shadow-sm relative">
                      {editMode && (
                        <div className="absolute top-2 right-2 flex items-center gap-1">
                          {/* Botões de reordenação */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => movePillarUp(pillarIndex)}
                            disabled={pillarIndex === 0}
                            className="text-[#8F7AC5] hover:bg-[#8F7AC5]/10 p-1 h-7 w-7"
                            title="Mover para cima"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => movePillarDown(pillarIndex)}
                            disabled={pillarIndex === planContent.pillars.length - 1}
                            className="text-[#8F7AC5] hover:bg-[#8F7AC5]/10 p-1 h-7 w-7"
                            title="Mover para baixo"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removePillar(pillar.id)}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 h-7 w-7"
                            title="Remover pilar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Icon with color picker */}
                      <div className="relative">
                        <div 
                          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 cursor-pointer transition-transform hover:scale-105"
                          style={{ backgroundColor: `${pillar.color}20` }}
                          onClick={() => editMode && setShowIconPicker(showIconPicker === pillar.id ? null : pillar.id)}
                        >
                          <IconComponent className="w-6 h-6" style={{ color: pillar.color }} />
                        </div>
                        
                        {/* Icon & Color Picker */}
                        <AnimatePresence>
                          {editMode && showIconPicker === pillar.id && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute top-14 left-0 bg-white rounded-xl shadow-lg border border-[#FAE0D5] p-4 z-10 w-64"
                            >
                              <p className="text-xs text-[#9890A8] mb-2">Ícone:</p>
                              <div className="flex flex-wrap gap-2 mb-3">
                                {availableIcons.map(icon => {
                                  const Icon = icon.icon;
                                  return (
                                    <button
                                      key={icon.name}
                                      onClick={() => updatePillar(pillar.id, 'icon', icon.name)}
                                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                                        pillar.icon === icon.name ? 'bg-[#8F7AC5] text-white' : 'bg-[#FBF4FF] text-[#6B5B8C] hover:bg-[#D98DAE]/20'
                                      }`}
                                    >
                                      <Icon className="w-5 h-5" />
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-xs text-[#9890A8] mb-2">Cor:</p>
                              <div className="flex gap-2">
                                {availableColors.map(color => (
                                  <button
                                    key={color.name}
                                    onClick={() => updatePillar(pillar.id, 'color', color.bg)}
                                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                                      pillar.color === color.bg ? 'ring-2 ring-offset-2 ring-[#30244F]' : ''
                                    }`}
                                    style={{ backgroundColor: color.bg }}
                                  />
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Title */}
                      {editMode ? (
                        <Input
                          value={pillar.title}
                          onChange={(e) => updatePillar(pillar.id, 'title', e.target.value)}
                          className="font-semibold text-[#30244F] mb-2 border-[#FAE0D5]"
                        />
                      ) : (
                        <h4 className="font-semibold text-[#30244F] mb-2">{pillar.title}</h4>
                      )}
                      
                      {/* Main Value */}
                      {(pillar.mainValue !== undefined || editMode) && (
                        editMode ? (
                          <Input
                            value={pillar.mainValue || ''}
                            onChange={(e) => updatePillar(pillar.id, 'mainValue', e.target.value)}
                            className="text-xl font-bold mb-3 border-[#FAE0D5]"
                            style={{ color: pillar.color }}
                            placeholder="Valor principal (ex: 07:00)"
                          />
                        ) : pillar.mainValue && (
                          <p className="text-2xl font-bold mb-3" style={{ color: pillar.color }}>{pillar.mainValue}</p>
                        )
                      )}

                      {/* Instructions */}
                      {(pillar.instructions || editMode) && (
                        <ul className="space-y-2">
                          {(pillar.instructions || []).map((inst, i) => (
                            <li key={i} className="text-sm text-[#6B5B8C] flex items-start gap-2">
                              {editMode ? (
                                <>
                                  <Textarea
                                    value={inst}
                                    onChange={(e) => updatePillarInstruction(pillar.id, i, e.target.value)}
                                    className="flex-1 text-sm border-[#FAE0D5] min-h-[60px] resize-y"
                                    rows={2}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removePillarInstruction(pillar.id, i)}
                                    className="text-red-400 hover:text-red-600 p-1"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: pillar.color }} />
                                  {inst}
                                </>
                              )}
                            </li>
                          ))}
                          {editMode && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addPillarInstruction(pillar.id)}
                              className="text-[#8F7AC5] text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Adicionar instrução
                            </Button>
                          )}
                        </ul>
                      )}

                      {/* Meals (EDITÁVEL) */}
                      {(pillar.meals || editMode) && (
                        <div className="mt-3">
                          {editMode && <p className="text-xs text-[#9890A8] mb-2">Refeições:</p>}
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(pillar.meals || []).map((meal, i) => (
                              editMode ? (
                                <div key={i} className="flex items-center gap-1 bg-[#FAE0D5] rounded-full px-2 py-1">
                                  <Input
                                    value={meal}
                                    onChange={(e) => updatePillarMeal(pillar.id, i, e.target.value)}
                                    className="w-24 h-6 text-xs border-0 bg-transparent p-0"
                                  />
                                  <button
                                    onClick={() => removePillarMeal(pillar.id, i)}
                                    className="text-red-400 hover:text-red-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <span key={i} className="px-2 py-1 bg-[#FAE0D5] text-[#6B5B8C] rounded-full text-xs">
                                  {meal}
                                </span>
                              )
                            ))}
                          </div>
                          {editMode && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addPillarMeal(pillar.id)}
                              className="text-[#D98DAE] text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Adicionar refeição
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Note */}
                      {(pillar.note !== undefined || editMode) && (
                        editMode ? (
                          <Textarea
                            value={pillar.note || ''}
                            onChange={(e) => updatePillar(pillar.id, 'note', e.target.value)}
                            className="text-sm border-[#FAE0D5] mt-2"
                            placeholder="Nota adicional"
                          />
                        ) : pillar.note && (
                          <p className="text-sm text-[#9890A8] mt-2">{pillar.note}</p>
                        )
                      )}

                      {/* Signals (EDITÁVEL) */}
                      {(pillar.signals || editMode) && (
                        <div className="mt-3">
                          {(pillar.signals?.length > 0 || editMode) && (
                            <p className="text-sm text-[#9890A8] mb-2">Sinais a observar:</p>
                          )}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(pillar.signals || []).map((signal, i) => (
                              editMode ? (
                                <div key={i} className="flex items-center gap-1 bg-[#FBF4FF] rounded-full px-2 py-1">
                                  <Input
                                    value={signal}
                                    onChange={(e) => updatePillarSignal(pillar.id, i, e.target.value)}
                                    className="w-20 h-6 text-xs border-0 bg-transparent p-0"
                                  />
                                  <button
                                    onClick={() => removePillarSignal(pillar.id, i)}
                                    className="text-red-400 hover:text-red-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <span key={i} className="px-2 py-1 bg-[#FBF4FF] text-[#8F7AC5] rounded-full text-xs">
                                  {signal}
                                </span>
                              )
                            ))}
                          </div>
                          {editMode && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addPillarSignal(pillar.id)}
                              className="text-[#8F7AC5] text-xs"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Adicionar sinal
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Textos Livres */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#30244F] text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#D98DAE]" />
                  Informações Adicionais
                </h3>
                {editMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addFreeText}
                    className="border-[#D98DAE] text-[#D98DAE]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Texto
                  </Button>
                )}
              </div>
              
              {(!planContent.freeTexts || planContent.freeTexts.length === 0) ? (
                <div className="text-center py-6 text-[#9890A8]">
                  {editMode ? (
                    <p className="text-sm">Clique em &quot;Adicionar Texto&quot; para incluir informações extras para a mãe</p>
                  ) : (
                    <p className="text-sm">Nenhuma informação adicional</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {planContent.freeTexts.map((freeText, textIndex) => (
                    <div 
                      key={freeText.id} 
                      className="rounded-2xl p-4 relative"
                      style={{ backgroundColor: `${freeText.color}15`, borderLeft: `4px solid ${freeText.color}` }}
                    >
                      {editMode && (
                        <div className="absolute top-2 right-2 flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveFreeTextUp(textIndex)}
                            disabled={textIndex === 0}
                            className="text-[#D98DAE] hover:bg-[#D98DAE]/10 p-1 h-7 w-7"
                            title="Mover para cima"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveFreeTextDown(textIndex)}
                            disabled={textIndex === planContent.freeTexts.length - 1}
                            className="text-[#D98DAE] hover:bg-[#D98DAE]/10 p-1 h-7 w-7"
                            title="Mover para baixo"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFreeText(freeText.id)}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 h-7 w-7"
                            title="Remover"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {editMode ? (
                        <div className="space-y-3 pr-24">
                          <div className="flex gap-3 items-center">
                            <Input
                              value={freeText.title}
                              onChange={(e) => updateFreeText(freeText.id, 'title', e.target.value)}
                              className="flex-1 font-semibold border-[#FAE0D5]"
                              placeholder="Título da seção"
                            />
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-[#9890A8]">Cor:</Label>
                              <input
                                type="color"
                                value={freeText.color}
                                onChange={(e) => updateFreeText(freeText.id, 'color', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-0"
                              />
                            </div>
                          </div>
                          <Textarea
                            value={freeText.content}
                            onChange={(e) => updateFreeText(freeText.id, 'content', e.target.value)}
                            className="min-h-[100px] border-[#FAE0D5]"
                            placeholder="Digite o texto livre aqui... Este texto aparecerá para a mãe exatamente como você escrever."
                          />
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-bold text-[#30244F] mb-2" style={{ color: freeText.color }}>
                            {freeText.title}
                          </h4>
                          <p className="text-[#6B5B8C] whitespace-pre-wrap leading-relaxed">
                            {freeText.content}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Routine Timeline */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#8F7AC5]" />
                  Rotina Sugerida
                </h3>
                {editMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={addRoutineItem}
                    className="text-[#8F7AC5]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                )}
              </div>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#8F7AC5] to-[#D98DAE]" />
                <div className="space-y-4">
                  {planContent.routine.map((item, i) => (
                    <div key={i} className="flex items-center gap-4 pl-8 relative">
                      <div className="absolute left-2 w-4 h-4 bg-white border-2 border-[#8F7AC5] rounded-full" />
                      {editMode ? (
                        <>
                          <Input
                            value={item.time}
                            onChange={(e) => updateRoutineItem(i, 'time', e.target.value)}
                            className="w-28 text-sm border-[#FAE0D5]"
                            placeholder="Horário"
                          />
                          <Input
                            value={item.activity}
                            onChange={(e) => updateRoutineItem(i, 'activity', e.target.value)}
                            className="flex-1 text-sm border-[#FAE0D5]"
                            placeholder="Atividade"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRoutineItem(i)}
                            className="text-red-400 hover:text-red-600 p-1"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <div className="flex items-center">
                          <span className="text-[#8F7AC5] font-medium text-sm w-28">{item.time}</span>
                          <span className="text-[#6B5B8C] text-sm">{item.activity}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Priorities (SEMANAS DINÂMICAS) */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#F2ACAA]" />
                  Prioridades por Semana
                </h3>
                {editMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addWeek}
                    className="border-[#8F7AC5] text-[#8F7AC5]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Semana
                  </Button>
                )}
              </div>
              <div className="space-y-4 max-w-4xl mx-auto">
                {Object.entries(planContent.priorities).map(([week, items]) => (
                  <div key={week} className="bg-[#FBF4FF] rounded-2xl p-4 relative">
                    {editMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeWeek(week)}
                        className="absolute top-2 right-2 text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                    {editMode ? (
                      <Input
                        value={week}
                        onChange={(e) => renameWeek(week, e.target.value)}
                        className="font-medium text-[#8F7AC5] mb-3 border-[#D98DAE]/30 bg-white"
                      />
                    ) : (
                      <h4 className="font-medium text-[#8F7AC5] mb-3">{week}</h4>
                    )}
                    <ul className="space-y-2">
                      {items.map((item, j) => (
                        <li key={j} className="text-sm text-[#6B5B8C] flex items-start gap-2">
                          {editMode ? (
                            <>
                              <Textarea
                                value={item}
                                onChange={(e) => updatePriority(week, j, e.target.value)}
                                className="flex-1 text-sm border-[#D98DAE]/30 min-h-[50px] resize-y"
                                rows={2}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removePriority(week, j)}
                                className="text-red-400 hover:text-red-600 p-1"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4 text-[#D98DAE] flex-shrink-0 mt-0.5" />
                              {item}
                            </>
                          )}
                        </li>
                      ))}
                      {editMode && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addPriority(week)}
                          className="text-[#8F7AC5] text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Adicionar prioridade
                        </Button>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Expectations */}
            <div className="bg-gradient-to-r from-[#FAE0D5] to-[#FBF4FF] rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#D98DAE]" />
                  Expectativas Realistas
                </h3>
                {editMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={addExpectation}
                    className="text-[#8F7AC5]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {planContent.expectations.map((exp, i) => (
                  <div key={i} className="text-center relative bg-white/50 rounded-xl p-3">
                    {editMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeExpectation(i)}
                        className="absolute -top-2 -right-2 text-red-400 hover:text-red-600 p-1"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                    {editMode ? (
                      <>
                        <Input
                          value={exp.period}
                          onChange={(e) => updateExpectation(i, 'period', e.target.value)}
                          className="text-center font-semibold text-[#8F7AC5] mb-1 border-[#D98DAE]/30"
                          placeholder="Período"
                        />
                        <Textarea
                          value={exp.desc}
                          onChange={(e) => updateExpectation(i, 'desc', e.target.value)}
                          className="text-center text-sm border-[#D98DAE]/30 min-h-[60px] resize-y"
                          placeholder="Descrição"
                          rows={2}
                        />
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-[#8F7AC5]">{exp.period}</p>
                        <p className="text-sm text-[#6B5B8C]">{exp.desc}</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Final Message */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#30244F] mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#D98DAE]" />
                Mensagem Final
              </h3>
              {editMode ? (
                <Textarea
                  value={planContent.finalMessage}
                  onChange={(e) => setPlanContent({ ...planContent, finalMessage: e.target.value })}
                  className="min-h-[100px] border-[#FAE0D5]"
                  placeholder="Mensagem de encorajamento para os pais..."
                />
              ) : (
                <p className="text-[#6B5B8C] italic">{planContent.finalMessage}</p>
              )}
            </div>
          </div>
        )}
      </main>
      
      {/* Modal de Compartilhamento */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#30244F]">Compartilhar Plano</h3>
                <p className="text-sm text-[#6B5B8C] mt-2">
                  Envie este link para a mãe visualizar o plano de sono do {planContent.babyName}
                </p>
              </div>
              
              <div className="bg-[#FBF4FF] rounded-xl p-3 mb-4">
                <p className="text-xs text-[#6B5B8C] mb-2">Link de compartilhamento:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-white border border-[#FAE0D5] rounded-lg px-3 py-2 text-sm text-[#30244F] truncate"
                  />
                  <Button
                    onClick={copyShareLink}
                    className={linkCopied ? 'bg-[#7BC47F]' : 'bg-[#8F7AC5]'}
                    size="sm"
                  >
                    {linkCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-[#FAE0D5]"
                  onClick={() => setShowShareModal(false)}
                >
                  Fechar
                </Button>
                <Button
                  className="flex-1 bg-[#8F7AC5] hover:bg-[#7A68A8]"
                  onClick={() => {
                    openPreview();
                    setShowShareModal(false);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Abrir Preview
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Modal de Preview (para planos não aprovados) */}
        {showPreviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setShowPreviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FBF4FF] rounded-3xl shadow-xl w-full max-w-2xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header do Preview */}
              <div className="bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] rounded-t-3xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    <span className="text-sm font-medium">Preview - Como a Mãe Verá</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreviewModal(false)}
                    className="text-white hover:bg-white/20 p-1"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <h2 className="text-2xl font-bold text-center">Plano de Sono Personalizado</h2>
                <p className="text-center text-white/80 mt-1">{planContent.babyName} • {planContent.babyAge} meses</p>
              </div>
              
              {/* Conteúdo do Preview */}
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Análise */}
                {planContent.summary && (
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <h3 className="font-semibold text-[#30244F] mb-2 flex items-center gap-2">
                      <Eye className="w-4 h-4 text-[#8F7AC5]" />
                      Análise da Situação
                    </h3>
                    <p className="text-sm text-[#6B5B8C] whitespace-pre-wrap">{planContent.summary}</p>
                  </div>
                )}
                
                {/* Textos Livres */}
                {planContent.freeTexts?.length > 0 && planContent.freeTexts.map((ft, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border-l-4" style={{ borderLeftColor: ft.color }}>
                    <h4 className="font-semibold text-[#30244F] mb-2" style={{ color: ft.color }}>{ft.title}</h4>
                    <p className="text-sm text-[#6B5B8C] whitespace-pre-wrap">{ft.content}</p>
                  </div>
                ))}
                
                {/* Pilares */}
                {planContent.pillars?.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-[#30244F]">Os Pilares do Sono</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {planContent.pillars.slice(0, 6).map((pillar, i) => (
                        <div key={i} className="bg-white rounded-xl p-3 shadow-sm border-l-3" style={{ borderLeftColor: pillar.color, borderLeftWidth: '3px' }}>
                          <h4 className="font-semibold text-xs" style={{ color: pillar.color }}>{pillar.title}</h4>
                          {pillar.mainValue && <p className="text-sm font-bold mt-0.5" style={{ color: pillar.color }}>{pillar.mainValue}</p>}
                          {pillar.instructions?.length > 0 && (
                            <p className="text-xs text-[#9890A8] mt-1">{pillar.instructions.length} dicas</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Rotina */}
                {planContent.routine?.length > 0 && (
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <h3 className="font-semibold text-[#30244F] mb-2 text-sm">Rotina Sugerida</h3>
                    <div className="grid grid-cols-2 gap-1">
                      {planContent.routine.slice(0, 6).map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className="font-semibold text-[#8F7AC5]">{item.time}</span>
                          <span className="text-[#6B5B8C] truncate">{item.activity}</span>
                        </div>
                      ))}
                    </div>
                    {planContent.routine.length > 6 && (
                      <p className="text-xs text-[#9890A8] italic mt-1">+{planContent.routine.length - 6} mais...</p>
                    )}
                  </div>
                )}
                
                {/* Prioridades por Semana */}
                {planContent.priorities && Object.keys(planContent.priorities).length > 0 && (
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <h3 className="font-semibold text-[#30244F] mb-2 text-sm">Prioridades por Semana</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(planContent.priorities).slice(0, 4).map(([week, items], i) => (
                        <div key={i} className="border-l-2 border-[#D98DAE] pl-2">
                          <h4 className="font-medium text-xs text-[#8F7AC5]">{week}</h4>
                          <p className="text-xs text-[#6B5B8C]">{items.length} tarefas</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Expectativas Realistas */}
                {planContent.expectations?.length > 0 && (
                  <div className="bg-white rounded-xl p-3 shadow-sm">
                    <h3 className="font-semibold text-[#30244F] mb-2 text-sm">Expectativas Realistas</h3>
                    <div className="flex flex-wrap gap-1">
                      {planContent.expectations.map((exp, i) => (
                        <span key={i} className="bg-[#8F7AC5] text-white text-xs px-2 py-0.5 rounded-full font-medium">
                          {exp.period}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Mensagem Final */}
                {planContent.finalMessage && (
                  <div className="bg-gradient-to-r from-[#FAE0D5] to-[#F5E6FA] rounded-2xl p-4">
                    <p className="text-sm text-[#6B5B8C] italic text-center">{planContent.finalMessage}</p>
                    <p className="text-xs text-[#D98DAE] text-center mt-2 font-semibold">De mãe pra mãe, com carinho 💜</p>
                  </div>
                )}
              </div>
              
              {/* Footer do Preview */}
              <div className="p-4 border-t border-[#FAE0D5] bg-white rounded-b-3xl">
                <p className="text-xs text-center text-[#9890A8] mb-3">
                  ⚠️ Este é um preview. Após aprovar, a mãe poderá acessar o plano completo.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-[#FAE0D5]"
                    onClick={() => setShowPreviewModal(false)}
                  >
                    Continuar Editando
                  </Button>
                  <Button
                    className="flex-1 bg-[#7BC47F] hover:bg-[#5BA35F]"
                    onClick={() => {
                      setShowPreviewModal(false);
                      handleApprove();
                    }}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Aprovar Plano
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Modal */}
      <AnimatePresence>
        {showTemplateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTemplateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F5A623]/10 flex items-center justify-center">
                    <BookmarkPlus className="w-5 h-5 text-[#F5A623]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#30244F]">Salvar como Template</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTemplateModal(false)}
                  className="text-[#9890A8]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <p className="text-sm text-[#6B5B8C] mb-6">
                Salve este plano como template para reutilizar com outros bebês de perfil similar.
              </p>

              <div className="space-y-4">
                <div>
                  <Label className="text-[#30244F]">Nome do Template *</Label>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    placeholder="Ex: 6-8 meses - associação mamadeira"
                  />
                </div>

                <div>
                  <Label className="text-[#30244F]">Descrição (opcional)</Label>
                  <Textarea
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[80px]"
                    placeholder="Quando usar este template? Ex: Bebês que mamam para dormir..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1 border-[#FAE0D5]"
                  onClick={() => setShowTemplateModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-[#F5A623] hover:bg-[#E09500] text-white"
                  onClick={handleSaveAsTemplate}
                  disabled={!templateName.trim() || savingTemplate}
                >
                  {savingTemplate ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <BookmarkPlus className="w-4 h-4 mr-2" />
                  )}
                  Salvar Template
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPlanGeneratorPage;
